import { Box, Button, Flex, Text } from '@fullstory/components';
import { ActivityTab, JULY_TS, ReferencesTab } from '@fullstory/dqm-ui';
import { useAiSuggestionsExploreEnabled } from '@fullstory/library-ui';
import { Event, EventFilter, NativePlatform } from '@fullstory/proto/fs/api/segment';
import { DQMRow, VolumeTimeRange } from '@fullstory/proto/fs/services/dx-objects';
import {
  Classification,
  NamedElement,
  NamedElementRecord,
} from '@fullstory/proto/fs/services/search-settings';
import { Flag } from '@fullstory/proto/fs/shared/flags';
import { useFlags } from '@fullstory/react-hooks';
import { allSpecObjectsBySlug, fullstorySelectors, IssueSilenceUpdate } from '@fullstory/redux';
import { containsNonDescendantCombinator } from '@fullstory/selectors';
import {
  ActiveIndexContext,
  AppearancesContextWithOnPreview,
  OnPreviewFnWithIndex,
  SignalStudioComponentProps,
  SignalStudioContext,
  SignalStudioHighlight,
  SignalStudioPreviewResolvedEvent,
  usePreviewSearch,
} from '@fullstory/signal-studio-context';
import React, { useCallback, useContext, useEffect, useMemo, useState } from 'react';
import { FormattedMessage, useIntl } from 'react-intl';
import { useSelector } from 'react-redux';
import { NamedElementModalVariant } from '../components';
import { ModalBodyProperties } from '../components/NamedElementModal/NamedElementDetailsModal/ModalBodyProperties';
import {
  CaptureOptionsUpdate,
  NamedElementModalSource,
} from '../components/NamedElementModal/types';
import { ProgressBar } from '../components/ProgressBar';
import { SidebarNamedElement } from '../components/SidebarNamedElement';
import { NamedElementWithPreview } from '../types';
import { isPendingSuggestion, makePlatformSelectorDeps } from '../utils';
import { useClassificationRulesForElement } from './useClassificationRules';
import { useEditableNamedElementDetailView } from './useEditableNamedElementDetailView';
import { useMeasureSelector } from './useMeasureSelector';
import { useOptimizeUtil } from './useOptimizeUtil';
import { useRandomPreviewSearch } from './useRandomPreviewSearch';
import { useSharedNamedElementsActions } from './useSharedNamedElementsActions';
import EventSupport = DQMRow.EventSupport;

export interface useNamedElementSignalStudioProps {
  id?: string;
  duplicate?: boolean;
  disableEdit?: boolean;
  disableArchive?: boolean;
  initialElement?: Partial<NamedElement>;
  initialSpecSlugs?: string[];
  variant: NamedElementModalVariant;
  onSave: (
    namedElement: NamedElement,
    captureOptions: CaptureOptionsUpdate,
    silence: IssueSilenceUpdate | null,
    fsSpecSlugs: string[] | null,
    hasExistingIssue: boolean,
    classification: Classification | null,
  ) => void;
  onConfirm?: () => void;
  onCloseSignal: (saved?: boolean) => void;
  onArchive: (id: string) => void;
  onUnarchive: (id: string) => void;
  onRejectSuggestion?: (id: string, feedback: string) => void;
  isSuggestion?: boolean;
  initialPreview?: SignalStudioPreviewResolvedEvent;
  exploreSuggestedElements?: NamedElementWithPreview[];
  collapsed?: boolean;
}

export const useNamedElementSignalStudio = (
  _props: useNamedElementSignalStudioProps,
): {
  SignalStudio: React.ComponentType<SignalStudioComponentProps> | null;
  signalStudioProps: SignalStudioComponentProps;
  saveElement: () => void;
} => {
  const intl = useIntl();
  const [timeRange, setTimeRange] = useState(VolumeTimeRange.DAY_7);
  const SignalStudio = useContext(SignalStudioContext);

  // for viewAll cases we allow other elements to be selected and swapped out
  // we need to allow setting the props and re-loading the detail view accordingly
  const [props, setProps] = useState<useNamedElementSignalStudioProps>(_props);
  const [trimTrigger, setTrimTrigger] = useState<boolean>(false);
  const [initialPreviewState, setInitialPreviewState] = useState<string | undefined>(props.id);

  // Get classification data from hook
  const {
    classificationOptions,
    selectedClassification: existingClassification,
    loadingClassification,
  } = useClassificationRulesForElement(props.id);

  // Single UI state - starts with existing value, changes with user input
  const [selectedClassification, setSelectedClassification] = useState<Classification | null>(
    existingClassification,
  );

  // Update UI state when existing classification changes (when switching elements)
  useEffect(() => {
    setSelectedClassification(existingClassification);
  }, [existingClassification]);

  const switchToElement = useCallback(
    (element: NamedElement | null, initialPreview?: SignalStudioPreviewResolvedEvent) => {
      const id = element?.id || element?.suggestion?.id || undefined;
      setProps({
        ...props,
        id,
        initialElement: element || undefined,
        initialSpecSlugs: undefined,
        initialPreview,
        variant: element?.id ? NamedElementModalVariant.edit : NamedElementModalVariant.new,
        isSuggestion: isPendingSuggestion(element),
        collapsed: false,
      });
      setTrimTrigger(false);
      setSelectedClassification(null);
      if (initialPreview) {
        setInitialPreviewState(id);
      }
    },
    [props],
  );

  const [handledElements, setHandledElements] = useState(new Set<string>());
  const { handled: handledSuggestedElements, unhandled: unhandledSuggestedElements } =
    useMemo(() => {
      const handled = new Set<string>();
      const unhandled = new Set<string>();
      props.exploreSuggestedElements?.forEach(element => {
        const id = element.namedElement.suggestion?.id;
        if (id) {
          if (handledElements.has(id)) {
            handled.add(id);
          } else {
            unhandled.add(id);
          }
        }
      });
      return { handled, unhandled };
    }, [props.exploreSuggestedElements, handledElements]);

  const isSaveAndContinueMode = !!props.exploreSuggestedElements?.length;
  const isSaveAndExitMode =
    unhandledSuggestedElements.size === 1 && props.id && unhandledSuggestedElements.has(props.id);

  const onHandledElement = useCallback(
    (id: string) => {
      // set the handled element state no matter what
      setHandledElements(prev => new Set(prev).add(id));
      // and then, if this is save and continue mode, we need to move to the next unhandled element
      if (isSaveAndContinueMode) {
        const nextSuggestedElement = props.exploreSuggestedElements?.find(element => {
          const suggestionId = element.namedElement.suggestion?.id;
          return (
            suggestionId && unhandledSuggestedElements.has(suggestionId) && suggestionId !== id
          );
        });
        if (nextSuggestedElement) {
          switchToElement(nextSuggestedElement.namedElement, nextSuggestedElement.preview);
        }
      }
    },
    [
      props.exploreSuggestedElements,
      unhandledSuggestedElements,
      isSaveAndContinueMode,
      switchToElement,
    ],
  );

  const onSkipElement = useCallback(() => {
    if (props.id) {
      onHandledElement(props.id);
      if (isSaveAndExitMode) {
        props.onCloseSignal(false);
      }
    }
  }, [props.id, isSaveAndExitMode, onHandledElement, props.onCloseSignal]);

  const {
    details,
    initialDetails,
    hasUnsavedChanges,
    activityMonitoring,
    activityMonitoringError,
    auditHistory,
    objectReferences,
    properties,
    propertiesLoading,
    propertiesFailed,
    retryFailedProperties,
    elementPropertyRules,
    setSnooze,
    setDetails,
    issues,
    savedApiName,
  } = useEditableNamedElementDetailView({
    namedElementId: props.id,
    timeRange,
    initialElement: props.initialElement,
    duplicate: props.duplicate,
    isSuggestion: props.isSuggestion,
  });

  useEffect(() => {
    // We only auto-trim suggestions. If we don't have a suggestion, there's nothing to do.
    if (!props.isSuggestion || !details?.namedElement?.namedElement?.suggestion) {
      return;
    }

    // If we have a suggestion whose selector wasn't trimmed on the server, auto-trim it.
    if (details.namedElement.namedElement.suggestion.reqSelectorTrim !== 'TRIM_AI_BACKEND') {
      setTrimTrigger(true);
    }
  }, [props.isSuggestion, details?.namedElement?.namedElement?.suggestion]);

  const flags = useFlags();
  const canModifyElements = flags.hasBit(Flag.PermModifyNamedElements);
  const canModifyProperties = flags.hasBit(Flag.PermModifyCustomVars);
  const hasFSTA = flags.hasBit(Flag.FeatFSTA);
  const showMostRecentSession = flags.featureFlags().AppMostRecentSession;
  const supportsViewAll = useAiSuggestionsExploreEnabled();
  const { fetchTotalVolume } = useOptimizeUtil();
  const specObjectsBySlug = useSelector(allSpecObjectsBySlug);

  // set initial spec to either be the input prop, which is set when opening an Element from Recommendations page, or as an ai suggested spec
  const initialSpecSlugs = useMemo(() => {
    if (props.initialSpecSlugs) {
      return props.initialSpecSlugs;
    }
    if (!props.isSuggestion || !flags.featureFlags().AppSuggestedElementsSpec) {
      return undefined;
    }
    const suggSpecObject = Object.values(specObjectsBySlug).find(
      object =>
        object.active &&
        details?.namedElement?.namedElement?.suggestion?.spec &&
        object.slug === details?.namedElement?.namedElement?.suggestion?.spec,
    );
    // if the spec is already mapped, then don't use the suggestion
    return suggSpecObject && !suggSpecObject.mapping ? [suggSpecObject.slug] : undefined;
  }, [
    details?.namedElement?.namedElement?.suggestion?.spec,
    flags,
    props.initialSpecSlugs,
    props.isSuggestion,
    specObjectsBySlug,
  ]);

  const callbacks = useMemo(() => {
    if (!isSaveAndContinueMode) {
      return {
        onSave: props.onSave,
        onCloseSignal: props.onCloseSignal,
        onRejectSuggestion: props.onRejectSuggestion,
      };
    }
    // need to intercept for some overridden behavior
    const onRejectSuggestion = props.onRejectSuggestion;
    return {
      onSave: (
        namedElement: NamedElement,
        captureOptions: CaptureOptionsUpdate,
        silence: IssueSilenceUpdate | null,
        fsSpecSlugs: string[] | null,
        hasExistingIssue: boolean,
        classification: Classification | null,
      ) => {
        props.onSave(
          namedElement,
          captureOptions,
          silence,
          fsSpecSlugs,
          hasExistingIssue,
          classification,
        );
        onHandledElement(namedElement.id || namedElement.suggestion?.id || '');
      },
      onCloseSignal: (saved?: boolean) => {
        // if this is a close signal as a result of a save, we want to ignore it unless we are on the last suggestion
        // allow close signals otherwise (as a result of other paths)
        if (isSaveAndExitMode || !saved) {
          props.onCloseSignal(saved);
        }
      },
      onRejectSuggestion: onRejectSuggestion
        ? (id: string, feedback: string) => {
            onRejectSuggestion(id, feedback);
            onHandledElement(id);
          }
        : undefined,
    };
  }, [
    isSaveAndContinueMode,
    isSaveAndExitMode,
    onHandledElement,
    props.onSave,
    props.onCloseSignal,
    props.onRejectSuggestion,
    selectedClassification,
  ]);

  const {
    handleOnSaveNamedElement,
    handleArchiveNamedElement,
    validation,
    selectedFsSpecObjectSlugs,
    setSelectedFsSpecObjectSlugs,
    fsSpecEnabled,
    fsSpecRoleOptions,
    areRolesDirty,
    existingSpecObjects,
    confirmModal,
    rejectSuggestion,
  } = useSharedNamedElementsActions({
    details,
    onSave: callbacks.onSave,
    onClose: callbacks.onCloseSignal,
    onArchive: props.onArchive,
    onUnarchive: props.onUnarchive,
    onRejectSuggestion: callbacks.onRejectSuggestion,
    initialSpecSlugs,
  });

  const [activeIndex, setActiveIndex] = useState(0);
  const [previewState, setPreviewState] = useState(0);
  const incrementPreviewState = useCallback(() => setPreviewState(current => current + 1), []);
  const showDataDirect = useSelector(fullstorySelectors.getIsDataDirect);

  const eventFiltersPreview = useMemo(
    (): (EventFilter | null)[] => [
      // prefer click
      { click: { any: {} } },
      // but fall back to any activity if no click data is available
      null,
    ],
    [],
  );

  const fallbackDepsPreview = useMemo(
    () =>
      details?.namedElement?.namedElement?.platformSelectors.map(sel =>
        makePlatformSelectorDeps(sel),
      ) ?? [],
    [details?.namedElement?.namedElement?.platformSelectors],
  );

  const previewSearches = usePreviewSearch(
    eventFiltersPreview,
    fallbackDepsPreview,
    incrementPreviewState,
  );

  const { getRandomPreviewSearch } = useRandomPreviewSearch();

  const onRandomPreview = useCallback(() => {
    getRandomPreviewSearch().then(search => {
      previewSearches[activeIndex]?.replace?.(search.primary, search.refinements, true);
    });
  }, [activeIndex, previewSearches, getRandomPreviewSearch]);

  const onSetActiveIndex = useCallback(
    (index: number) => {
      const changed = index !== activeIndex;
      if (changed) {
        setActiveIndex(index);
        incrementPreviewState(); // index changes always trigger a preview state change
      }
    },
    [activeIndex, setActiveIndex, incrementPreviewState],
  );
  const onDeleteIndex = useCallback(
    (index: number) => {
      if ((index === activeIndex && activeIndex > 0) || index < activeIndex) {
        onSetActiveIndex(activeIndex - 1);
      }
      previewSearches[index]?.remove?.();
    },
    [activeIndex, onSetActiveIndex, previewSearches],
  );

  const { measureSelector } = useMeasureSelector();

  const onInspectSelector = useCallback(
    (
      selector: string,
      canonicalizedSelector: string,
      nativePlatform: NativePlatform | undefined,
    ) => {
      if (details?.namedElement?.namedElement) {
        const platformSelectors = details.namedElement.namedElement.platformSelectors;
        const platformSelectorForUpdate = platformSelectors[activeIndex];
        if (platformSelectorForUpdate) {
          setDetails({
            ...details,
            namedElement: {
              ...details.namedElement,
              namedElement: {
                ...details.namedElement.namedElement,
                platformSelectors: [
                  ...platformSelectors.slice(0, activeIndex),
                  {
                    ...platformSelectorForUpdate,
                    selector,
                    originalSelector: selector,
                    canonicalizedSelector,
                    platforms:
                      nativePlatform &&
                      !platformSelectorForUpdate.platforms.includes(nativePlatform)
                        ? [...platformSelectorForUpdate.platforms, nativePlatform]
                        : platformSelectorForUpdate.platforms,
                  },
                  ...platformSelectors.slice(activeIndex + 1),
                ],
              },
            },
          });

          if (flags.featureFlags().AppMeasureDataStudio) {
            measureSelector(canonicalizedSelector);
          }

          const shouldAutoTrim = !containsNonDescendantCombinator(selector);
          setTrimTrigger(shouldAutoTrim);
        }
      }
    },
    [activeIndex, details, setDetails],
  );

  const searchEventForMostRecentSession: Event | undefined = useMemo(() => {
    if (!props.id || !showMostRecentSession || !hasFSTA) return undefined;
    const searchEvent: Event = {
      primary: {
        click: {
          any: {},
        },
      },
      deps: [
        {
          element: {
            withElementId: {
              value: [props.id],
              spec: [],
            },
          },
        },
      ],
    };
    return searchEvent;
  }, [props.id, showMostRecentSession, hasFSTA]);

  const tabs = useMemo(() => {
    if (!props.id || props.isSuggestion) {
      return supportsViewAll
        ? // in the view all case, we need to keep the tab for stable previews when switching between create/edit
          { titles: [], content: [] }
        : undefined;
    }

    const titles = [
      intl.formatMessage({ defaultMessage: 'Properties', id: 'aI80kg' }),
      intl.formatMessage({ defaultMessage: 'Activity', id: 'ZmlNQ3' }),
      intl.formatMessage({ defaultMessage: 'References', id: 'nCQY4D' }),
    ];
    const elementIdentifiers = {
      id: props.id,
      name: initialDetails?.namedElement?.namedElement?.name ?? '',
    };

    const content = [
      {
        key: 'properties',
        node: (
          <ModalBodyProperties
            elementIdentifiers={elementIdentifiers}
            properties={properties || []}
            propertiesLoading={propertiesLoading}
            propertiesFailed={propertiesFailed}
            retryFailedProperties={retryFailedProperties}
            elementPropertyRules={elementPropertyRules}
            canModifyProperties={canModifyProperties}
            existingSpecObjects={existingSpecObjects}
          />
        ),
      },
      {
        key: 'activity',
        node: (
          <ActivityTab
            activityMonitoring={activityMonitoring || null}
            activityMonitoringError={activityMonitoringError || null}
            objectType="Element"
            monitoringStart={JULY_TS}
            auditHistory={auditHistory || null}
            onTimeRangeChange={setTimeRange}
            timeRange={timeRange}
            showDataDirect={showDataDirect}
            searchEvent={searchEventForMostRecentSession}
            eventSupport={EventSupport.EVENT_SUPPORT_SUPPORTED}
          />
        ),
      },
      {
        key: 'references',
        node: <ReferencesTab objectReferences={objectReferences || null} objectType="Element" />,
      },
    ];
    return { titles, content };
  }, [
    activityMonitoring,
    activityMonitoringError,
    auditHistory,
    canModifyProperties,
    elementPropertyRules,
    objectReferences,
    properties,
    propertiesFailed,
    propertiesLoading,
    props.id,
    retryFailedProperties,
    showDataDirect,
    timeRange,
    intl,
    searchEventForMostRecentSession,
    initialDetails?.namedElement?.namedElement?.name,
    supportsViewAll,
    props.isSuggestion,
    existingSpecObjects,
  ]);

  const onPreview: OnPreviewFnWithIndex = useCallback(
    (primary, refinements, _, index) => {
      previewSearches[index]?.replace?.(primary, refinements, true);
    },
    [previewSearches],
  );

  const [hoveredSelector, setHoveredSelector] = useState<string | undefined>(undefined);

  // we want the sidebar to force re-mount as different elements are selected
  const configSidebarKey =
    details?.namedElement?.namedElement?.id ??
    details?.namedElement?.namedElement?.suggestion?.id ??
    '';
  const ConfigSidebar = (
    <AppearancesContextWithOnPreview onPreview={onPreview}>
      <ActiveIndexContext.Provider
        value={{ index: activeIndex, setIndex: onSetActiveIndex, deleteIndex: onDeleteIndex }}
      >
        <SidebarNamedElement
          key={configSidebarKey}
          details={details}
          setDetails={setDetails}
          setSnooze={setSnooze}
          issues={issues}
          selectedFsSpecObjectSlugs={selectedFsSpecObjectSlugs}
          setSelectedFsSpecObjectSlugs={setSelectedFsSpecObjectSlugs}
          selectedClassification={selectedClassification}
          loadingClassification={loadingClassification}
          classificationOptions={classificationOptions}
          onClassificationChange={setSelectedClassification}
          fsSpecEnabled={fsSpecEnabled}
          fsSpecRoleOptions={fsSpecRoleOptions}
          variant={props.variant}
          validation={validation}
          savedApiName={savedApiName}
          onHoverSelector={setHoveredSelector}
          trimTrigger={trimTrigger}
          setTrimTrigger={setTrimTrigger}
          isSuggestion={props.isSuggestion || false}
        />
        {confirmModal}
      </ActiveIndexContext.Provider>
    </AppearancesContextWithOnPreview>
  );
  const ConfigSidebarMeta = props.exploreSuggestedElements?.length ? (
    <Flex column gap={8} alignItems="stretch">
      <Flex gap={8} justifyContent="space-between" alignItems="center">
        <Box flexShrink={0}>
          <Text appearance="bodyMedium" noWrap>
            <FormattedMessage
              defaultMessage="{handled} / {total} {total, plural, one {suggestion} other {suggestions}} reviewed"
              id="cGAm56"
              values={{
                handled: handledSuggestedElements.size,
                total: props.exploreSuggestedElements.length,
              }}
            />
          </Text>
        </Box>
        {props.id && unhandledSuggestedElements.has(props.id) && (
          <Button size="small" onClick={onSkipElement}>
            {isSaveAndExitMode ? (
              <FormattedMessage defaultMessage="Skip and Exit" id="e0cn9J" />
            ) : (
              <FormattedMessage defaultMessage="Skip" id="/4tOwT" />
            )}
          </Button>
        )}
      </Flex>
      <ProgressBar value={handledSuggestedElements.size / props.exploreSuggestedElements.length} />
    </Flex>
  ) : undefined;

  const metadata = useMemo(() => {
    if (!details?.namedElement?.metadata || props.variant === NamedElementModalVariant.new) {
      return undefined;
    }

    return {
      createdBy: details.namedElement.metadata.createdBy,
      updatedBy: details.namedElement.metadata.lastUpdatedBy,
      createdAt: details.namedElement.metadata.created,
      updatedAt: details.namedElement.metadata.lastUpdated,
    };
  }, [details?.namedElement?.metadata, props.variant]);

  const highlights = useMemo(() => {
    const highlights: SignalStudioHighlight[] = [];

    const activePlatformSelector =
      details?.namedElement?.namedElement?.platformSelectors?.[activeIndex];
    if (activePlatformSelector) {
      highlights.push({
        type: 'inspected',
        target: activePlatformSelector,
      });
    }

    if (hoveredSelector) {
      highlights.push({
        type: 'inspected-context',
        target: {
          platforms: [],
          selector: hoveredSelector,
        },
      });
    }

    return highlights;
  }, [activeIndex, details?.namedElement?.namedElement?.platformSelectors, hoveredSelector]);

  const saveElement = useCallback(() => {
    handleOnSaveNamedElement(
      initialDetails,
      details,
      props.variant,
      NamedElementModalSource.settings,
      props.isSuggestion,
      selectedClassification,
    );
  }, [
    details,
    handleOnSaveNamedElement,
    initialDetails,
    props.isSuggestion,
    props.variant,
    selectedClassification,
  ]);

  return {
    SignalStudio,
    signalStudioProps: {
      header: details?.namedElement?.namedElement?.name ?? '',
      lowerConfigPartName: intl.formatMessage({ defaultMessage: 'selector', id: 'A7ml5w' }),
      configSidebar: props.collapsed ? undefined : ConfigSidebar,
      configSidebarMeta: props.collapsed ? undefined : ConfigSidebarMeta,
      tabs,
      preview: {
        state: previewState,
        initialPreview: props.initialPreview,
        initialPreviewState,
        index: activeIndex,
        onIndexChange: onSetActiveIndex,
        mode: 'element-inspector',
        previews: previewSearches.map(search => ({
          search,
        })),
        highlights,
        onInspectSelector,
        onRandomPreview,
        viewAllElements: {
          supported: !isSaveAndContinueMode, // Disable "View All Elements" in bulk approval mode
          initialValue: !!(props.collapsed && props.initialPreview),
          onClick: (element: NamedElementRecord) => switchToElement(element.namedElement),
        },
      },
      onConfirm: async () => {
        if (!details?.namedElement?.namedElement) return;

        if (props.onConfirm) {
          const volumes = await Promise.all(
            details.namedElement.namedElement.platformSelectors.map(selector =>
              fetchTotalVolume(selector.selector),
            ),
          );
          const totalVolume = volumes.reduce((acc, curr) => acc + curr, 0);
          // if element is watched, it's okay to have no volume, since interactions are not assumed
          if (totalVolume > 0 || details.watched) {
            saveElement();
          } else {
            props.onConfirm();
          }
        } else {
          saveElement();
        }
      },
      confirmLabel: isSaveAndExitMode
        ? intl.formatMessage({ defaultMessage: 'Save and Exit', id: 'PDtJxC' })
        : isSaveAndContinueMode
        ? intl.formatMessage({ defaultMessage: 'Save and Continue', id: '40TKTx' })
        : undefined,
      hasUnsavedChanges: hasUnsavedChanges || areRolesDirty,
      onCloseSignal: () => {
        props.onCloseSignal(false);
      },
      onArchive:
        props.id && !props.isSuggestion ? () => handleArchiveNamedElement(true) : undefined,
      onUnarchive: props.id
        ? () => {
            handleArchiveNamedElement(false);
          }
        : undefined,
      onRejectSuggestion: props.isSuggestion
        ? (feedback: string) => rejectSuggestion(props.id!, feedback)
        : undefined,
      isSuggestion: props.isSuggestion,
      archived: details?.namedElement?.metadata?.archived || false,
      disableEdit:
        props.disableEdit || details?.namedElement?.metadata?.archived || !canModifyElements,
      disableArchive:
        (props.variant === NamedElementModalVariant.new && !props.isSuggestion) ||
        props.disableArchive ||
        props.disableEdit ||
        !canModifyElements,
      emptyMessage: intl.formatMessage(
        {
          defaultMessage: 'Element Preview<b></b>Load preview to start defining your element',
          id: 'Qopba4',
        },
        {
          b: () => <br />,
        },
      ),
      metadata,
    },
    saveElement,
  };
};
